'use client';

import React from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'framer-motion';
import { containerStagger, fadeUp } from '@/lib/animations';

/**
 * TeamSection
 *
 * Renders a visually engaging section introducing the Andoria AI team.
 * - Displays a header and grid of team members with images, names, roles, and descriptions.
 * - Uses Framer Motion for entrance and hover animations, respecting user reduced motion preferences.
 * - Responsive grid adapts for mobile, tablet, and desktop layouts.
 *
 * Team member data is hardcoded for clarity and maintainability.
 */
const TeamSection: React.FC = () => {
  // Detects if user prefers reduced motion for accessibility
  const reduce = useReducedMotion();

  // List of team members with image, name, role, and description
  const teamMembers = [
    {
      image: '/images/about/andrew-oriakhi-andoria-ai.webp',
      name: 'Andrew Oriakhi',
      role: 'Technical Consultant',
      description:
        'Certified Cloud Data Engineer with 10+ years leading implementations across banking, retail, e-commerce, government, and digital services.',
    },
    {
      image: '/images/about/Oluwatomisin_Fawehinmi_andoria-ai.webp',
      name: 'Tomisin Fawehinmi',
      role: 'Project Manager',
      description:
        'Results-driven Project Manager orchestrating digital products via agile delivery, clear communication, and cross-functional synergy.',
    },
    {
      image: '/images/about/Joshua-aluola-andoria-ai.webp',
      name: 'Joshua Aluola',
      role: 'Product Engineer',
      description:
        'Builds fast, data-driven applications with clean, secure code that connects the UI with solid, well-structured systems behind the scenes.',
    },
    {
      image: '/images/about/david-ose-andoria-ai.webp',
      name: 'David Ose',
      role: 'Product Designer',
      description:
        'Product Designer with a focus on intuitive, user-centered design and scalable digital experiences across all platforms.',
    },
  ];

  return (
    <section className="py-20 px-6 mx-auto">
      {/* Main motion container for header and grid */}
      <motion.div
        className="space-y-12"
        initial={reduce ? undefined : 'hidden'}
        whileInView={reduce ? undefined : 'visible'}
        viewport={{ once: true, amount: 0.15 }}
        variants={containerStagger}
      >
        {/* Header: Section title and subtitle */}
        <motion.div variants={fadeUp}>
          <div className="inline-flex items-center justify-center gap-2.5 px-4 py-2 rounded-2xl border-[1.5px] border-dashed border-[#92d9ee] mb-6">
            <span className="gradient-text bg-clip-text text-transparent font-semibold text-base font-manrope">
              Meet Our Team
            </span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-semibold font-manrope tracking-[-0.96px] leading-[56px] gradient-text bg-clip-text text-transparent max-w-2xl">
            All is quite useless without proper teamwork!
          </h2>
        </motion.div>

        {/* Team Grid: Each member displayed in a card with image and info */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={containerStagger}
        >
          {teamMembers.map((member, index) => (
            <motion.div
              key={index}
              className="group"
              variants={fadeUp}
              whileHover={reduce ? undefined : { scale: 1.03 }}
              transition={{ type: 'spring', stiffness: 220, damping: 18 }}
            >
              {/* Card: Team member image, overlay, and info */}
              <div className="relative w-full h-[600px] rounded-2xl overflow-hidden transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-xl">
                <Image
                  src={member.image}
                  alt={member.name}
                  fill
                  className="object-cover transition-transform duration-300 group-hover:scale-110"
                />
                {/* Overlay gradient for readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                {/* Info block: Name, role, description */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-3">{member.name}</h3>
                  <p className="text-lg font-semibold mb-3">{member.role}</p>
                  <p className="text-sm">{member.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default TeamSection;
